import { Box, Chip, Divider, Stack, Text } from '@ltvco/refresh-lib/theme';

const labelSx = {
  fontWeight: 700,
  fontSize: '0.82rem',
  color: 'text.secondary',
  minWidth: 160,
  flexShrink: 0,
};

const valueSx = {
  fontSize: '0.9rem',
  color: 'text.primary',
  wordBreak: 'break-word',
  minWidth: 0,
};

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={2}
      sx={{ py: 1.2, overflow: 'hidden' }}
    >
      <Text sx={labelSx}>{label}</Text>
      {children}
    </Stack>
  );
}

function ConnectivityChip({ label, value }: { label: string; value: string }) {
  const active =
    typeof value === 'boolean' ? value : String(value).toLowerCase() === 'yes';
  const subtext = active ? 'Yes' : 'No';
  return (
    <Chip
      label={`${label}: ${String(subtext).toUpperCase()}`}
      size="small"
      sx={{
        fontFamily: "'Trebuchet MS', sans-serif",
        fontWeight: 700,
        fontSize: '0.68rem',
        letterSpacing: '0.06em',
        backgroundColor: active ? 'success.light' : 'background.light',
        color: active ? 'success.dark' : 'text.secondary',
        border: 'none',
        height: 26,
      }}
    />
  );
}

export default function GSMADeviceDetails({
  deviceid = '',
  refcode = '',
  manufacturer = '',
  marketingname = '',
  modelname = '',
  devicetype = '',
  operatingsys = '',
  nfc = 'no',
  bluetooth = 'no',
  wlan = 'no',
  band = '',
}) {
  return (
    <Box
      id="device-fraud-details-section"
      sx={{
        backgroundColor: 'background.paper',
        borderColor: 'divider',
        border: 1,
        borderRadius: '12px',
        px: { xs: 2.5, sm: 4 },
        py: 2.5,
        width: '100%',
        mt: 2,
      }}
    >
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
        <Text
          sx={{
            fontWeight: 700,
            fontSize: '1.05rem',
            color: 'text.primary',
          }}
        >
          Device &amp; registry details
        </Text>
      </Stack>

      <Divider sx={{ mb: 0.5 }} />

      {/* Rows */}
      <Row label="Device ID (IMEI)">
        <Text sx={valueSx}>{deviceid || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Reference Code">
        <Text sx={valueSx}>{refcode || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Manufacturer">
        <Text sx={valueSx}>{manufacturer || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Marketing Name">
        <Text sx={valueSx}>{marketingname || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Model Name">
        <Text sx={valueSx}>{modelname || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Device Type">
        <Text sx={valueSx}>{devicetype || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Operating System">
        <Text sx={valueSx}>{operatingsys || '—'}</Text>
      </Row>
      <Divider />

      <Row label="Network bands">
        <Text sx={valueSx}>{band || '—'}</Text>
      </Row>
      <Divider />

      <Row label="NFC · Bluetooth · WLAN">
        <Stack direction="row" spacing={1} flexWrap="wrap">
          <ConnectivityChip label="NFC" value={nfc} />
          <ConnectivityChip label="BLUETOOTH" value={bluetooth} />
          <ConnectivityChip label="WLAN" value={wlan} />
        </Stack>
      </Row>
    </Box>
  );
}
